import React, { useState } from "react";
import { useGetReleases, useDeleteRelease, useDownloadRelease } from "./hooks";
import { Release } from "./api";
import NewReleaseModal from "./NewReleaseModal";
import { useI18n } from "../../i18n";

interface ReleasesListProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ReleasesList: React.FC<ReleasesListProps> = ({
  searchQuery,
  onSearchChange,
}) => {
  const { t, code } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: releases = [], isLoading, error } = useGetReleases(searchQuery);
  const deleteReleaseMutation = useDeleteRelease();
  const downloadReleaseMutation = useDownloadRelease();

  const handleDelete = (id: string, releaseName: string) => {
    if (window.confirm(t("releases.confirmDelete", { name: releaseName }))) {
      deleteReleaseMutation.mutate(id);
    }
  };

  const handleDownload = (id: string) => {
    downloadReleaseMutation.mutate(id);
  };

  const handleEdit = (release: Release) => {
    setEditingRelease(release);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingRelease(null);
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "major release":
        return "px-2.5 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full";
      case "minor update":
        return "px-2.5 py-1 text-xs font-semibold text-purple-800 bg-purple-100 rounded-full";
      case "bug fix":
        return "px-2.5 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full";
      case "security update":
        return "px-2.5 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full";
      default:
        return "px-2.5 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full";
    }
  };

  const getReleaseIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "major release":
        return {
          icon: "fa-rocket",
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600",
        };
      case "minor update":
        return {
          icon: "fa-plus",
          bgColor: "bg-purple-100",
          iconColor: "text-purple-600",
        };
      case "bug fix":
        return {
          icon: "fa-bug",
          bgColor: "bg-green-100",
          iconColor: "text-green-600",
        };
      case "security update":
        return {
          icon: "fa-wrench",
          bgColor: "bg-orange-100",
          iconColor: "text-orange-600",
        };
      default:
        return {
          icon: "fa-file",
          bgColor: "bg-gray-100",
          iconColor: "text-gray-600",
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(releases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReleases = releases.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">
          {t("releases.loadFailed")}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Table Controls - exact match from HTML */}
      <div className="p-4 border-b border-gray-200 bg-gray-50/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center space-x-4">
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                placeholder={t("releases.searchPlaceholder")}
                value={searchQuery}
                onChange={onSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition"
              />
              <i className="fa-solid fa-search text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
              <i className="fa-solid fa-filter mr-2"></i> {t("common.filter")}
            </button>
            <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
              <i className="fa-solid fa-file-export mr-2"></i> {t("common.export")}
            </button>
          </div>
        </div>
      </div>

      {/* Table - exact match from HTML */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                />
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("releases.table.release")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("releases.table.version")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("releases.table.type")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("releases.table.date")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("releases.table.downloads")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider text-right"
              >
                {t("releases.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {releases.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <i className="fa-solid fa-inbox text-gray-400 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {searchQuery ? t("releases.empty.noResultsTitle") : t("releases.empty.emptyTitle")}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6">
                      {searchQuery
                        ? t("common.tryAdjustingSearch")
                        : t("releases.empty.emptyBody")}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => {
                          // This will trigger the parent component to open the modal
                          const uploadButton = document.querySelector(
                            '[data-testid="upload-release-btn"]'
                          ) as HTMLButtonElement;
                          if (uploadButton) uploadButton.click();
                        }}
                        className="px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center"
                      >
                        <i className="fa-solid fa-upload mr-2"></i>
                        {t("releases.uploadFirstRelease")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              currentReleases.map((release: Release) => {
                const iconConfig = getReleaseIcon(release.type);
                return (
                  <tr key={release.id} className="hover:bg-gray-50/50">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                      />
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 ${iconConfig.bgColor} rounded-lg flex items-center justify-center mr-3`}
                        >
                          <i
                            className={`fa-solid ${iconConfig.icon} ${iconConfig.iconColor} text-sm`}
                          ></i>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {release.releaseName}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {release.description || t("common.noDescription")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 whitespace-nowrap font-medium text-gray-900">
                      v{release.version}
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <span className={getTypeBadgeColor(release.type)}>
                        {release.type}
                      </span>
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      {formatDate(release.createdAt)}
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      {release.downloads}
                    </td>
                    <td className="p-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(release)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t("releases.editTooltip")}
                        >
                          <i className="fa-solid fa-edit text-sm"></i>
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(release.id, release.releaseName)
                          }
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t("releases.deleteTooltip")}
                        >
                          <i className="fa-solid fa-trash text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - exact match from HTML */}
      {totalPages > 1 && (
        <div className="p-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between text-xs sm:text-sm border-t border-gray-200">
          <p className="text-gray-600">
            {t("releases.showing", {
              from: startIndex + 1,
              to: Math.min(endIndex, releases.length),
              total: releases.length,
            })}
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <i className="fa-solid fa-chevron-left text-xs"></i>
            </button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 border rounded-md font-medium ${
                    currentPage === page
                      ? "border-gray-800 bg-gray-800 text-white"
                      : "border-gray-300 hover:bg-gray-100 transition-colors"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <i className="fa-solid fa-chevron-right text-xs"></i>
            </button>
          </div>
        </div>
      )}

      {/* Edit Release Modal */}
      <NewReleaseModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        editRelease={editingRelease}
        onSuccess={() => {
          handleCloseEditModal();
        }}
      />
    </div>
  );
};

export default ReleasesList;
